import React, { FC, useEffect } from 'react';
import { RoundIcon, RoundIconVariants } from '@components/atoms/RoundIcon';
import { Container } from '@components/atoms/Container';
import { Anchor, AnchorMode } from '@components/atoms/Anchor';
import { LanguagesDropdown } from './LanguagesDropdown';
import { UserProfileDropdown } from './UserProfileDropdown';
import styles from './Header.module.css';
import { useThemes } from '@hooks/useThemes';
import { useData } from '@hooks/useData';
import { OVER_18_KEY, SITE_URLS } from '@constants';
import { useGetUser } from '@services/useGetUser';
import { Modules, useModule } from '@hooks/useModule';
import { useUpdateUserAge } from '@services/useUpdateUserAge';
import { clearFromSessionStorage, loadFromSessionStorage } from '@utils/storage';

export interface HeaderProps {
    mode?: 'with-separator' | 'logo-only';
}

export const Header: FC<HeaderProps> = ({ mode }) => {
    const { isModuleActive } = useModule();
    const { allSettings, login, cart, user } = useData();
    const { useGrayscaleTheme, toggleGrayscaleTheme, increaseFontSize, decreaseFontSize } = useThemes();
    const { request: requestUser, error: errorGetUser, lastResponseTime: lastGetUser } = useGetUser();
    const { request: requestUpdateUserAge } = useUpdateUserAge();

    const marketplaceCanMakeSales = !isModuleActive(Modules.catalogMode) || isModuleActive(Modules.paymentAndPickupAtStore);

    // if there is logged in user, get the user's information
    useEffect(() => {
        if (login !== null) {
            requestUser();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [login]);

    // after getting logged in user's information
    // check for a mismatch between +18 from api and from session
    // if there is a mismatch, means that user was not logged in and was prompted the +18 question, then logged in
    // use api to update user's +18 info
    useEffect(() => {
        if (lastGetUser) {
            if (!errorGetUser) {
                const infoIsOver18 = loadFromSessionStorage(OVER_18_KEY);
                if (
                    (user?.data?.over18 === false && infoIsOver18 === 'true') ||
                    (user?.data?.over18 === true && infoIsOver18 === 'false')
                ) {
                    requestUpdateUserAge({ over_18: infoIsOver18 === 'true' });
                    clearFromSessionStorage(OVER_18_KEY);
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lastGetUser]);

    useEffect(() => {
        if (user?.data?.photo && login?.data?.user) {
            login.data.user.photo = user.data.photo;
        }
    }, [user, login]);

    const logoItem = allSettings?.data?.layouts?.find((item) => item.name === 'logo');
    const logoSrc = logoItem?.value;

    return (
        <header
            className={
                // themes
                `${useGrayscaleTheme ? styles['theme-grayscale'] : ''}` +
                // other classes
                ` ${styles.header} py-3`
            }>
            <Container>
                <>
                    <div className="d-flex flex-wrap align-items-center justify-content-center justify-content-lg-start">
                        <Anchor
                            href={SITE_URLS.home}
                            classes="d-flex align-items-center link-body-emphasis text-decoration-none"
                            mode={AnchorMode.none}>
                            <img className={styles['brand-logo']} src={logoSrc} alt={allSettings?.data?.marketplaceName} />
                        </Anchor>
                        <div className="nav ms-auto">
                            {(!mode || mode !== 'logo-only') && (
                                <>
                                    {/* decrease font size */}
                                    <div className="me-3 align-self-center d-none d-md-block">
                                        <RoundIcon
                                            variant={RoundIconVariants.variant1}
                                            text="A–"
                                            onClick={() => decreaseFontSize()}
                                        />
                                    </div>

                                    {/* increase font size */}
                                    <div className="me-3 align-self-center d-none d-md-block">
                                        <RoundIcon
                                            variant={RoundIconVariants.variant1}
                                            text="A+"
                                            onClick={() => increaseFontSize()}
                                        />
                                    </div>

                                    {/* grayscale theme */}
                                    <div className="me-3 me-sm-4 align-self-center d-none d-md-block">
                                        <RoundIcon
                                            variant={RoundIconVariants.variant1}
                                            active={useGrayscaleTheme}
                                            icon={`${useGrayscaleTheme ? 'fa-solid' : 'fa-light'} fa-browser`}
                                            onClick={() => toggleGrayscaleTheme()}
                                        />
                                    </div>

                                    {/* language dropdown */}
                                    <div className="me-3 me-sm-4 align-self-center">
                                        <LanguagesDropdown />
                                    </div>

                                    {/* user dropdown */}
                                    <div className={marketplaceCanMakeSales ? 'me-3 me-sm-4' : ''}>
                                        <UserProfileDropdown user={login?.data?.user} />
                                    </div>

                                    {marketplaceCanMakeSales && (
                                        <div className="d-flex align-items-center">
                                            <Anchor href="shopping-cart" mode={AnchorMode.none} classes="text-decoration-none">
                                                <RoundIcon
                                                    variant={RoundIconVariants.variant1}
                                                    icon="fa-light fa-basket-shopping"
                                                    indicator={cart?.data?.nProducts || undefined}
                                                />
                                            </Anchor>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                    {mode === 'with-separator' && <div className={styles['header-separator']}></div>}
                </>
            </Container>
        </header>
    );
};
